/**
 * Consolidated utility functions for Pokemon data handling
 */
import api from "./api";

// Pokemon API cache
const pokemonCache = new Map();
const spriteCache = new Map();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Format a replay link to ensure consistent format
 */
export const formatReplayLink = (rawUrl) => {
  if (!rawUrl) return '';

  let url = rawUrl.trim();

  // Handle the case where user just pastes a replay ID directly
  if (/^[a-zA-Z0-9-]+$/.test(url) && !url.includes('.')) {
    return `https://replay.pokemonshowdown.com/${url}`;
  }

  if (!url.startsWith('http')) {
    url = `https://${url}`;
  }

  // Fix double replay.replay issue by first standardizing URL
  url = url.replace(/replay\.replay\./g, 'replay.');
  
  // NO LONGER CONVERTING rereplay to replay - "rereplay" is a valid Showdown domain
  // if (url.includes('rereplay.')) {
  //   url = url.replace('rereplay.', 'replay.');
  // }

  // Ensure URL uses https
  if (url.startsWith('http:')) {
    url = url.replace('http:', 'https:');
  }

  return url;
};

/**
 * Check if a string is a valid showdown replay link
 */
export const validateReplayLink = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  // Format the URL first to handle common issues
  const formattedUrl = formatReplayLink(url);
  
  // Regular expressions for validating replay URLs
  const showdownReplayRegex = /^https?:\/\/(replay|replays)\.pokemonshowdown\.com\/[a-zA-Z0-9\-]+$/;
  const longReplayIDRegex = /^https?:\/\/replay\.pokemonshowdown\.com\/[a-z0-9]+-[0-9]+$/;
  
  // Check against both patterns
  return showdownReplayRegex.test(formattedUrl) || longReplayIDRegex.test(formattedUrl);
};

/**
 * Get a Pokemon sprite URL by name
 */
export const getPokemonSpriteUrl = async (name) => {
  if (!name) return null;
  
  // Check cache first
  if (spriteCache.has(name)) {
    const { url, timestamp } = spriteCache.get(name);
    // Check if cache is still valid
    if (Date.now() - timestamp < CACHE_DURATION) {
      return url;
    }
  }
  
  try {
    // Convert name to lowercase for consistent lookup
    const normalizedName = normalizePokemonName(name);
    
    // Default sprite format
    let spriteUrl = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${normalizedName}.png`;
    
    // Try to get a better sprite from PokéAPI if possible
    const pokemonDetails = await fetchPokemonDetails(name);
    if (pokemonDetails && pokemonDetails.sprites && pokemonDetails.sprites.front_default) {
      spriteUrl = pokemonDetails.sprites.front_default;
    }
    
    // Cache result
    spriteCache.set(name, { 
      url: spriteUrl, 
      timestamp: Date.now() 
    });
    
    return spriteUrl;
  } catch (error) {
    console.error('Error fetching sprite:', error);
    return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/0.png`;
  }
};

/**
 * Normalize a Pokemon name for consistent comparison
 */
export const normalizePokemonName = (name) => {
  if (!name) return '';
  
  // Remove non-alphanumeric characters and convert to lowercase
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim();
};

/**
 * Get the base species name from a Pokemon name (removing form variants)
 * e.g., "Zamazenta-Crowned" -> "Zamazenta"
 */
export const getBaseSpeciesName = (name) => {
  if (!name) return '';
  
  // Handle special cases first
  const specialCases = {
    'Urshifu-Single Strike': 'Urshifu',
    'Urshifu-Rapid Strike': 'Urshifu',
    'Ogerpon-Wellspring Mask': 'Ogerpon',
    'Ogerpon-Hearthflame Mask': 'Ogerpon',
    'Ogerpon-Cornerstone Mask': 'Ogerpon',
    'Ogerpon-Teal Mask': 'Ogerpon',
    'Tauros-Paldea': 'Tauros-Paldea', // Keep regional forms distinct
    'Tauros-Paldea-Fire': 'Tauros-Paldea',
    'Tauros-Paldea-Water': 'Tauros-Paldea',
    'Tauros-Paldea-Combat': 'Tauros-Paldea',
  };
  
  if (specialCases[name]) {
    return specialCases[name];
  }
  
  // General rules for form variants
  // Remove common form suffixes
  const formSuffixes = [
    '-Crowned',
    '-Hero',
    '-Origin',
    '-Altered',
    '-Therian',
    '-Incarnate',
    '-Complete',
    '-10%',
    '-50%',
    '-Unbound',
    '-Confined',
    '-Mega',
    '-Mega-X',
    '-Mega-Y',
    '-Primal',
    '-Ash',
    '-Blade',
    '-Sky',
    '-Land',
    '-Sunshine',
    '-Rainy',
    '-Snowy',
    '-Dusk',
    '-Dawn',
    '-Midnight',
    '-School',
    '-Solo',
    '-Ice',
    '-Shadow',
    '-Rider',
    '-Disguised',
    '-Busted',
    '-Hangry',
    '-Full Belly',
    '-Gmax',
    '-Eternamax',
    '-Low Key',
    '-Amped',
    '-Noice',
    '-Ice Face',
    '-Blue-Striped',
    '-White-Striped',
    '-Gulping',
    '-Gorging',
    '-East',
    '-West',
    '-Neutral',
    '-Heat',
    '-Wash',
    '-Frost',
    '-Fan',
    '-Mow',
    '-Attack',
    '-Defense',
    '-Speed',
    '-Sandy',
    '-Trash',
    '-Plant',
    '-Zen',
    '-Galar-Zen',
    '-Pirouette',
    '-Baile',
    '-Pom-Pom',
    '-Pa\'u',
    '-Sensu',
    '-Meteor',
    '-Core',
  ];
  
  let baseName = name;
  
  // Check each suffix and remove if found
  for (const suffix of formSuffixes) {
    if (baseName.endsWith(suffix)) {
      baseName = baseName.substring(0, baseName.length - suffix.length);
      break;
    }
  }
  
  // Handle some special patterns
  if (baseName.includes('-')) {
    // Keep Alolan/Galarian/Hisuian/Paldean forms
    if (baseName.includes('-Alola') || baseName.includes('-Galar') || 
        baseName.includes('-Hisui') || baseName.includes('-Paldea')) {
      return baseName;
    }
    
    // For other hyphenated names, check if it's a known Pokemon
    const beforeHyphen = baseName.split('-')[0];
    const knownHyphenatedPokemon = [
      'Ho-Oh', 'Porygon-Z', 'Type: Null', 'Tapu Koko', 'Tapu Lele', 
      'Tapu Bulu', 'Tapu Fini', 'Wo-Chien', 'Chien-Pao', 'Ting-Lu', 'Chi-Yu'
    ];
    
    if (!knownHyphenatedPokemon.includes(baseName)) {
      // If not a known hyphenated Pokemon, it's probably a form
      baseName = beforeHyphen;
    }
  }
  
  return baseName.trim();
};

/**
 * Fetch Pokemon details from the API
 */
export const fetchPokemonDetails = async (name) => {
  if (!name) return null;
  
  const normalizedName = normalizePokemonName(name);
  
  // Check cache first
  if (pokemonCache.has(normalizedName)) {
    const { data, timestamp } = pokemonCache.get(normalizedName);
    // Check if cache is still valid
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }
  
  try {
    const response = await api.get(`/api/pokemon/${normalizedName}`);
    
    // Cache result
    pokemonCache.set(normalizedName, {
      data: response.data,
      timestamp: Date.now()
    });
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching Pokemon details for ${name}:`, error);
    return null;
  }
};

/**
 * Fetch all Pokemon names for validation
 */
export const fetchAllPokemonNames = async () => {
  try {
    const response = await api.get('/api/pokemon/names');
    return response.data;
  } catch (error) {
    console.error('Error fetching Pokemon names:', error);
    return [];
  }
};

/**
 * Parse team data in Showdown format
 */
export const parsePokePaste = (pasteText) => {
  if (!pasteText) return [];

  const lines = pasteText.split('\n');
  const pokemon = [];
  let currentPokemon = null;

  for (let line of lines) {
    line = line.trim();
    
    // Skip empty lines
    if (!line) continue;
    
    // New Pokemon entry starts with a name
    if (!line.startsWith('Ability:') && 
        !line.startsWith('EVs:') && 
        !line.startsWith('IVs:') && 
        !line.startsWith('-') && 
        !line.startsWith('Level:') && 
        !line.startsWith('Shiny:') && 
        !line.includes('@')) {
      
      // If we already have a Pokemon being processed, add it to our list
      if (currentPokemon) {
        pokemon.push(currentPokemon);
      }
      
      // Start a new Pokemon
      currentPokemon = { 
        name: line.split('(')[0].trim(),
        items: [],
        ability: '',
        nature: '',
        evs: {},
        moves: []
      };
      
      // Check for held item
      if (line.includes('@')) {
        currentPokemon.item = line.split('@')[1].trim();
      }
    } 
    // Parse ability
    else if (line.startsWith('Ability:')) {
      currentPokemon.ability = line.replace('Ability:', '').trim();
    }
    // Parse EVs
    else if (line.startsWith('EVs:')) {
      const evString = line.replace('EVs:', '').trim();
      const evParts = evString.split('/');
      
      for (const evPart of evParts) {
        const [value, stat] = evPart.trim().split(' ');
        currentPokemon.evs[stat] = parseInt(value);
      }
    }
    // Parse nature
    else if (line.includes('Nature')) {
      currentPokemon.nature = line.split(' ')[0].trim();
    }
    // Parse moves
    else if (line.startsWith('-')) {
      const move = line.substring(1).trim();
      currentPokemon.moves.push(move);
    }
  }
  
  // Add the last Pokemon
  if (currentPokemon) {
    pokemon.push(currentPokemon);
  }
  
  return pokemon;
};

/**
 * Process Showdown replay data to extract match details
 */
export const processReplayData = async (replayData) => {
  if (!replayData) return null;
  
  try {
    // Extract player and opponent names
    const playerName = replayData.p1 || '';
    const opponentName = replayData.p2 || '';
    
    // Extract format (e.g., gen9vgc2023, gen9vgc2024regulation, etc.)
    const format = replayData.format || '';
    
    // Extract Pokémon teams
    const playerTeam = replayData.p1team ? replayData.p1team.map(p => p.species) : [];
    const opponentTeam = replayData.p2team ? replayData.p2team.map(p => p.species) : [];
    
    // Extract lead Pokémon (first Pokémon sent out)
    const playerLeads = [];
    const opponentLeads = [];
    
    // Determine the winner
    let result = 'tie';
    if (replayData.winner === playerName) {
      result = 'win';
    } else if (replayData.winner === opponentName) {
      result = 'loss';
    }
    
    // Parse the log to extract leads
    if (replayData.log) {
      const lines = replayData.log.split('\n');
      let inTeamPreview = false;
      
      for (const line of lines) {
        // Check for team preview
        if (line.includes('|teampreview|')) {
          inTeamPreview = true;
        }
        
        // Look for Pokémon being sent out at the start of the battle
        if (line.includes('|switch|p1a:') && playerLeads.length < 2) {
          const parts = line.split('|');
          if (parts.length >= 4) {
            const pokemonName = parts[3].split(',')[0];
            if (!playerLeads.includes(pokemonName)) {
              playerLeads.push(pokemonName);
            }
          }
        }
        
        if (line.includes('|switch|p2a:') && opponentLeads.length < 2) {
          const parts = line.split('|');
          if (parts.length >= 4) {
            const pokemonName = parts[3].split(',')[0];
            if (!opponentLeads.includes(pokemonName)) {
              opponentLeads.push(pokemonName);
            }
          }
        }
        
        // Exit team preview
        if (inTeamPreview && line.includes('|start|')) {
          inTeamPreview = false;
        }
      }
    }
    
    return {
      playerName,
      opponentName,
      format,
      playerTeam,
      opponentTeam,
      playerLeads,
      opponentLeads,
      result
    };
  } catch (error) {
    console.error('Error processing replay data:', error);
    return null;
  }
};